import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ArtifactContentRenderer - Renders artifact content by type
 *
 * Supports rendering charts, tables, markdown, fusion fragments, and more.
 */
import { FusionFragmentHandler, FusionFragmentProvider } from '@vertesia/fusion-ux';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { VegaLiteChart } from '../../features/agent/chat/VegaLiteChart';
import { useCodeBlockContext } from './CodeBlockContext';
import { CodeBlockErrorBoundary, CodeBlockPlaceholder } from './CodeBlockPlaceholder';
/**
 * Auto-detect render type from content and path
 */
function autoDetectRenderType(content, path, contentType) {
    const ext = path.split('.').pop()?.toLowerCase();
    // SVG files → mockup renderer (inline SVG from text content)
    if (ext === 'svg') {
        return 'mockup';
    }
    // Image extensions (binary → rendered via URL)
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext || '')) {
        return 'image';
    }
    // Markdown
    if (ext === 'md') {
        return 'markdown';
    }
    // CSV as table
    if (ext === 'csv') {
        return 'table';
    }
    // JSON content - try to detect chart or table
    if (contentType === 'json' && typeof content === 'object' && content !== null) {
        const obj = content;
        // Check for Vega-Lite schema
        if (typeof obj.$schema === 'string' && obj.$schema.includes('vega')) {
            return 'vega-lite';
        }
        // Check for wrapped Vega-Lite
        if (obj.library === 'vega-lite' && 'spec' in obj) {
            return 'vega-lite';
        }
        // Check for fusion fragment template with data
        if ('template' in obj && 'data' in obj) {
            return 'fusion-fragment';
        }
        // Check for table data (array of objects)
        if (Array.isArray(content) && content.length > 0 && typeof content[0] === 'object') {
            return 'table';
        }
    }
    // Default to code
    return 'code';
}
function toVegaLiteSpec(content) {
    if (typeof content !== 'object' || content === null) {
        return null;
    }
    const obj = content;
    if (obj.library === 'vega-lite' && 'spec' in obj && typeof obj.spec === 'object' && obj.spec !== null) {
        return obj;
    }
    if (typeof obj.$schema === 'string' && obj.$schema.includes('vega')) {
        return { library: 'vega-lite', spec: obj };
    }
    return null;
}
/**
 * Table renderer for array data or CSV
 */
function TableRenderer({ content }) {
    const { headers, rows } = useMemo(() => {
        if (!Array.isArray(content) || content.length === 0) {
            return { headers: [], rows: [] };
        }
        // Extract headers from first object
        const first = content[0];
        if (typeof first !== 'object' || first === null) {
            return { headers: [], rows: [] };
        }
        const headers = Object.keys(first);
        const rows = content.map(row => headers.map(h => {
            const val = row[h];
            if (val === null || val === undefined)
                return '';
            if (typeof val === 'object')
                return JSON.stringify(val);
            return String(val);
        }));
        return { headers, rows };
    }, [content]);
    if (headers.length === 0) {
        return (_jsx(CodeBlockPlaceholder, { type: "table", error: "No table data found" }));
    }
    return (_jsxs("div", { className: "overflow-x-auto", children: [_jsxs("table", { className: "min-w-full text-sm border-collapse", children: [_jsx("thead", { children: _jsx("tr", { className: "border-b", children: headers.map((h, i) => (_jsx("th", { className: "px-3 py-2 text-left font-medium text-muted", children: h }, i))) }) }), _jsx("tbody", { children: rows.slice(0, 100).map((row, i) => (_jsx("tr", { className: "border-b border-muted/20", children: row.map((cell, j) => (_jsx("td", { className: "px-3 py-2", children: cell }, j))) }, i))) })] }), rows.length > 100 && (_jsxs("div", { className: "text-sm text-muted py-2", children: ["Showing 100 of ", rows.length, " rows"] }))] }));
}
/**
 * Code block renderer for raw content
 */
function CodeRenderer({ content, path }) {
    const ext = path.split('.').pop()?.toLowerCase() || '';
    const code = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    return (_jsx("pre", { className: "overflow-x-auto p-3 bg-muted/10 rounded text-sm", children: _jsx("code", { className: `language-${ext}`, children: code }) }));
}
/**
 * Sanitize SVG markup using DOMPurify.
 * Allows only safe SVG elements; strips scripts, event handlers, and foreignObject.
 */
export function sanitizeSvg(svg) {
    return DOMPurify.sanitize(svg, {
        USE_PROFILES: { svg: true, svgFilters: true },
        ADD_TAGS: ['use'],
        FORBID_TAGS: ['foreignObject'],
    });
}
/**
 * Make SVG responsive — remove fixed dimensions, ensure viewBox.
 */
export function makeSvgResponsive(svg) {
    return svg.replace(/<svg([^>]*)>/i, (_full, attrs) => {
        let a = attrs;
        if (!/viewBox/i.test(a)) {
            const w = /\swidth\s*=\s*["']?(\d+(?:\.\d+)?)/i.exec(a);
            const h = /\sheight\s*=\s*["']?(\d+(?:\.\d+)?)/i.exec(a);
            if (w && h)
                a += ` viewBox="0 0 ${w[1]} ${h[1]}"`;
        }
        a = a
            .replace(/\swidth\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i, '')
            .replace(/\sheight\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i, '');
        if (/style="/i.test(a)) {
            a = a.replace(/style="([^"]*)"/i, (_, v) => `style="${v};width:100%;height:auto;display:block;max-width:100%;"`);
        }
        else {
            a += ' style="width:100%;height:auto;display:block;max-width:100%;"';
        }
        if (!/preserveAspectRatio=/i.test(a)) {
            a += ' preserveAspectRatio="xMidYMid meet"';
        }
        return `<svg${a}>`;
    });
}
/**
 * Mockup renderer — renders raw SVG content inline after sanitization.
 */
function MockupRenderer({ content }) {
    const processedSvg = useMemo(() => {
        const raw = typeof content === 'string' ? content.trim() : '';
        if (!raw)
            return null;
        return makeSvgResponsive(sanitizeSvg(raw));
    }, [content]);
    if (!processedSvg) {
        return _jsx(CodeBlockPlaceholder, { type: "expand", error: "Empty mockup" });
    }
    return (_jsx("div", { style: { margin: '16px 0', width: '100%', overflowX: 'auto' }, dangerouslySetInnerHTML: { __html: processedSvg } }));
}
/**
 * Image renderer
 */
function ImageRenderer({ content, path }) {
    const url = typeof content === 'string' ? content : '';
    const alt = path.split('/').pop() || 'Artifact image';
    return (_jsx("img", { src: url, alt: alt, className: "max-w-full h-auto rounded", loading: "lazy" }));
}
/**
 * ArtifactContentRenderer - Main component
 *
 * Renders artifact content based on explicit type or auto-detection.
 */
export function ArtifactContentRenderer({ content, renderType, path, runId, contentType, }) {
    const { MarkdownRenderer } = useCodeBlockContext();
    // Determine actual render type
    const actualType = useMemo(() => {
        if (renderType !== 'auto') {
            return renderType;
        }
        return autoDetectRenderType(content, path, contentType);
    }, [content, path, contentType, renderType]);
    // Render based on type
    switch (actualType) {
        case 'chart': {
            const spec = toVegaLiteSpec(content);
            if (!spec) {
                return (_jsx(CodeBlockPlaceholder, { type: "chart", error: "Only Vega-Lite charts are supported. Recharts rendering has been retired." }));
            }
            return (_jsx(CodeBlockErrorBoundary, { type: "chart", fallbackCode: JSON.stringify(content, null, 2), children: _jsx(VegaLiteChart, { spec: spec, artifactRunId: runId }) }));
        }
        case 'vega-lite': {
            const spec = toVegaLiteSpec(content);
            if (!spec) {
                return (_jsx(CodeBlockPlaceholder, { type: "chart", error: "Invalid Vega-Lite specification" }));
            }
            return (_jsx(CodeBlockErrorBoundary, { type: "chart", fallbackCode: JSON.stringify(content, null, 2), children: _jsx(VegaLiteChart, { spec: spec, artifactRunId: runId }) }));
        }
        case 'fusion-fragment': {
            // For fusion fragments, content should have { template, data }
            // Wrap with FusionFragmentProvider to inject VegaLiteChart for embedded charts
            const fragmentContent = content;
            console.log('[ArtifactContentRenderer] fusion-fragment:', {
                hasTemplate: !!fragmentContent.template,
                hasData: !!fragmentContent.data,
                runId,
                hasVegaLiteChart: !!VegaLiteChart,
            });
            if (fragmentContent.template && fragmentContent.data) {
                return (_jsx(CodeBlockErrorBoundary, { type: "fusion-fragment", fallbackCode: JSON.stringify(content, null, 2), children: _jsx(FusionFragmentProvider, { data: fragmentContent.data, ChartComponent: VegaLiteChart, artifactRunId: runId, children: _jsx(FusionFragmentHandler, { code: JSON.stringify(fragmentContent.template), data: fragmentContent.data }) }) }));
            }
            // If no data wrapper, treat as template-only (needs context from parent provider)
            return (_jsx(CodeBlockErrorBoundary, { type: "fusion-fragment", fallbackCode: JSON.stringify(content, null, 2), children: _jsx(FusionFragmentHandler, { code: JSON.stringify(content) }) }));
        }
        case 'table':
            return (_jsx(CodeBlockErrorBoundary, { type: "table", fallbackCode: JSON.stringify(content, null, 2), children: _jsx(TableRenderer, { content: content }) }));
        case 'markdown': {
            const markdownContent = typeof content === 'string' ? content : String(content);
            return (_jsx(CodeBlockErrorBoundary, { type: "markdown", fallbackCode: markdownContent, children: MarkdownRenderer ? (_jsx(MarkdownRenderer, { artifactRunId: runId, children: markdownContent })) : (_jsx("pre", { className: "overflow-x-auto p-3 bg-muted/10 rounded text-sm", children: _jsx("code", { children: markdownContent }) })) }));
        }
        case 'mockup':
            return (_jsx(CodeBlockErrorBoundary, { type: "expand", fallbackCode: typeof content === 'string' ? content : path, children: _jsx(MockupRenderer, { content: content }) }));
        case 'image':
            return (_jsx(CodeBlockErrorBoundary, { type: "image", fallbackCode: path, children: _jsx(ImageRenderer, { content: content, path: path }) }));
        case 'code':
        default:
            return _jsx(CodeRenderer, { content: content, path: path });
    }
}
//# sourceMappingURL=ArtifactContentRenderer.js.map